// Which client is on screen: the one Claude left (`src/client/ai`) or the one
// the human team is building (`src/client/human`), chosen at the App level.
//
// Composition-root code, like `main.tsx`: it is the one place besides that
// file allowed to import both halves of the client, because its whole job is
// to pick between them. **The toggle goes to whoever may see the dev tools**
// (`useDevTools`: a dev or e2e build, else an approved account that may read
// `Dev`). Anybody else gets the AI client, and a stored choice is ignored, not
// erased.
//
// Plain DOM and inline style on purpose — no kit. The kit is the AI client's,
// and a switch that depends on one of the things it switches between would
// leave the other side unable to draw it.

import { useCallback, useState, type ReactNode } from "react";

import type { EngineGame } from "src/engine/api/createGame";
import type { GameStore } from "src/engine/game/GameStore";

import { App as AiApp } from "src/client/ai/App";
import HumanApp from "src/client/human/App";
import { ClientSwitchContext } from "src/client/clientSwitchContext";
import { useDevTools } from "src/client/ai/ui/useDevTools";
import { ExperimentsBadgeContext } from "src/client/experimentsBadgeContext";
import { ExperimentsBadge } from "src/client/ai/ui/ExperimentsBadge";
import { ThemeProvider, THEME } from "src/client/ai/ui/kit";

/**
 * What the root built and both clients read: the one store, and the one
 * `Game` over it. The human client takes the `Game` alone — it is the
 * contract's — and the AI client takes both, because it still reads the store.
 */
export interface Shared {
  store: GameStore;
  game: EngineGame;
}

export type ClientId = "ai" | "human";

interface Client {
  id: ClientId;
  label: string;
  App: (props: Shared) => ReactNode;
}

/** In cycling order. Add a third here and the button cycles through it. */
const CLIENTS: readonly Client[] = [
  { id: "ai", label: "AI", App: ({ store, game }) => <AiApp store={store} game={game} /> },
  { id: "human", label: "Human", App: ({ game }) => <HumanApp game={game} /> }
];

const KEY = "factory-chess:client";

/** The stored choice, or the AI client. Wrapped: storage can throw or be empty. */
function loadClient(): ClientId {
  try {
    const stored = localStorage.getItem(KEY);
    return CLIENTS.some((c) => c.id === stored) ? (stored as ClientId) : "ai";
  } catch {
    return "ai";
  }
}

function saveClient(id: ClientId): void {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    // A private window or blocked storage: the choice lasts the tab, no more.
  }
}

/**
 * The A/B badge both clients render (`ExperimentsBadgeContext`). **Wrapped in
 * the kit's theme**: the badge is drawn with the kit, and the human client
 * mounts a theme of its own above wherever it renders this — without the wrap
 * `theme.kit` is undefined there and the badge throws.
 */
const BADGE = (
  <ThemeProvider theme={THEME}>
    <ExperimentsBadge />
  </ThemeProvider>
);

/**
 * Mounts the chosen client and, where switchable, provides it a button
 * that cycles to the next one (`ClientSwitchContext`) — **the client renders
 * the button, not this**, so it sits inside whichever tree is up rather than
 * over it. **Switching remounts the client, not the game**: the store
 * and the `Game` are the root's and are handed to whichever client is up, so
 * the board carries across. What does not is each client's own React state —
 * which screen it was on, what was selected — because that tree is gone.
 */
export function ClientSwitch({ store, game }: Shared) {
  const switchable = useDevTools();
  const [stored, setId] = useState<ClientId>(loadClient);
  const id: ClientId = switchable ? stored : "ai";
  const current = CLIENTS.find((c) => c.id === id) ?? CLIENTS[0];
  const next = CLIENTS[(CLIENTS.indexOf(current) + 1) % CLIENTS.length];

  const cycle = useCallback(() => {
    saveClient(next.id);
    setId(next.id);
  }, [next.id]);

  const button = switchable ? (
    <button
      type="button"
      onClick={cycle}
      title={`Showing the ${current.label} client. Click for ${next.label}.`}
      data-testid="client-toggle"
      data-client={current.id}
      className="client-switch"
      style={current.id === "ai" ? {
        font: "600 11px/1 system-ui, sans-serif",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "6px 10px",
        borderRadius: 4,
        border: "1px solid rgba(255,255,255,0.35)",
        background: current.id === "ai" ? "#2f6a9c" : "#b25a1f",
        color: "#fff",
        cursor: "pointer",
        opacity: 0.85
      } : undefined}
    >
      Client: {current.label}
    </button>
  ) : null;

  return (
    <ClientSwitchContext.Provider value={button}>
      <ExperimentsBadgeContext.Provider value={BADGE}>
        <current.App store={store} game={game} />
      </ExperimentsBadgeContext.Provider>
    </ClientSwitchContext.Provider>
  );
}
